import { isNil } from "lodash";
import moment from "moment-timezone";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { FORMAT_TIME } from "src/utils/formatters/dates";
import { oldPadLeadingZeros } from "./numbers";

const padL2 = (n: number) => oldPadLeadingZeros(n, 2);

export interface TimeOptions {
    withDp1?: boolean;
    withSeconds?: boolean;
}

/**
 * Format two times into a slot
 * @param startTime Start time
 * @param endTime End time
 * @returns Slot
 */
export function formatTimeSlots(
    startTime: string,
    endTime: string,
    translateCommon: TFunction,
    opts?: TimeOptions
): string {
    const startDate = formatDurationToTimeLt(startTime, translateCommon, opts);
    const endDate = formatDurationToTimeLt(endTime, translateCommon, opts);

    return `${startDate} - ${endDate}`;
}

export function parseTime(
    time: string,
    translateCommon: TFunction
): { hours: number; minutes: number; seconds: number } | null | undefined {
    if (time === null || time === undefined) {
        return time as null | undefined;
    }

    const hasDp1 = time.endsWith(`(${translateCommon("dates.dp1-suffix")})`);
    const [hours, minutes, seconds] = time
        .replace(`(${translateCommon("dates.dp1-suffix")})`, "")
        .split(":")
        .map((n) => Number.parseInt(n, 10));

    return {
        hours: hours + (hasDp1 ? 24 : 0),
        minutes,
        seconds,
    };
}

export function parseLtTime(
    time: string,
    translateCommon: TFunction
): { hours: number; minutes: number; seconds: number } | null | undefined {
    if (time === null || time === undefined) {
        return time as null | undefined;
    }

    const hasDp1 = time.includes(`(${translateCommon("dates.dp1-suffix")})`);
    const timeFormat = moment(
        time.replace(`(${translateCommon("dates.dp1-suffix")})`, ""),
        FORMAT_TIME
    ).format("HH:mm:ss");
    const [hours, minutes, seconds] = timeFormat.split(":").map((n) => Number.parseInt(n, 10));

    return {
        hours: hours + (hasDp1 ? 24 : 0),
        minutes,
        seconds,
    };
}

/**
 * Format a time
 * @param hours Number of hours
 * @param minutes Number of minutes
 * @param seconds Number of seconds
 * @param options Options
 * @returns Formatted time
 */
export function formatTime(
    hours: number,
    minutes: number,
    _translateCommon: TFunction,
    seconds?: number,
    { withDp1 = false, withSeconds = false }: TimeOptions = {}
): string {
    const timeSlot = [
        padL2(withDp1 ? hours % 24 : hours),
        padL2(minutes),
        withSeconds && padL2(seconds ?? 0),
    ]
        .filter(Boolean)
        .join(":");

    return timeSlot;
}

/**
 * Format a time LT
 * @param timeSlot slot like 14:30
 * @param options Options
 * @returns Formatted time
 */
export function formatTimeSlot(timeSlot: string, translateCommon: TFunction): string {
    const hours = parseInt(timeSlot.split(":")[0], 10);
    const minutes = parseInt(timeSlot.split(":")[1], 10);
    let formatTimeSlot = `${padL2(hours)}:${padL2(minutes)}`;

    if (hours >= 24) {
        const newHour = padL2(hours - 24);
        formatTimeSlot = newHour.concat(":", padL2(minutes));
    }

    return moment(formatTimeSlot, FORMAT_TIME)
        .format(FORMAT_TIME)
        .concat(hours >= 24 ? ` (${translateCommon("dates.dp1-suffix")})` : "");
}

/**
 * Format a local hour to LT
 * @param localHour slot like 14
 * @param options Options
 * @returns Formatted time
 */
export function formatLocalHour(localHour: number, translateCommon: TFunction): string {
    let hour = localHour;

    if (localHour >= 24) {
        hour = localHour - 24;
    }

    return moment
        .utc(moment.duration(hour, "h").asMilliseconds())
        .format(FORMAT_TIME)
        .concat(localHour >= 24 ? ` (${translateCommon("dates.dp1-suffix")})` : "");
}

/**
 * Format a time from a string
 * @param time Time to format
 * @param opts Options
 * @returns Formatted time
 */
export function formatStringTime(
    time: string,
    translateCommon: TFunction,
    opts?: TimeOptions
): string {
    if (time === null || time === undefined) {
        return time as null | undefined;
    }

    const [hours, minutes] = time.split(":").map((val) => parseInt(val, 10));
    return formatTime(hours, minutes, translateCommon, undefined, opts);
}

/**
 * Format a time from a string to LT format
 * @param time Time to format
 * @param opts Options
 * @returns Formatted time
 */
export function formatStringTimeLt(time: string, translateCommon: TFunction): string {
    if (time === null || time === undefined) {
        return time as null | undefined;
    }

    return formatTimeSlot(time, translateCommon);
}

/**
 * Format a time from a duration
 * @param ipt Duration input
 * @param opts Options
 * @returns Formatted duration
 */
export function formatDurationToTime(
    ipt: moment.DurationInputArg1,
    translateCommon: TFunction,
    { ipt2, ...opts }: TimeOptions & { ipt2?: moment.DurationInputArg2 } = {}
): string {
    if (ipt === null || ipt === undefined) {
        return ipt as null | undefined;
    }

    const duration = moment.duration(ipt, ipt2);
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    return formatTime(hours + days * 24, minutes, translateCommon, seconds, opts);
}

/**
 * Format a LT time from a duration
 * @param ipt Duration input
 * @param opts Options
 * @returns Formatted duration
 */
export function formatDurationToTimeLt(
    ipt: moment.DurationInputArg1,
    translateCommon: TFunction,
    { ipt2 }: TimeOptions & { ipt2?: moment.DurationInputArg2 } = {}
): string {
    if (ipt === null || ipt === undefined) {
        return ipt as null | undefined;
    }

    const duration = moment.duration(ipt, ipt2);
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    const slot = `${padL2(hours + days * 24)}:${padL2(minutes)}`;

    return formatTimeSlot(slot, translateCommon);
}

/**
 * Format a time to a duration
 * @param time Time to format
 * @returns Duration
 */
export function formatTimeToDuration(
    time: string,
    translateCommon: TFunction,
    { withDp1 = false }: TimeOptions = {}
): moment.Duration | null | undefined {
    if (time === null || time === undefined) {
        return time as null | undefined;
    }

    const { hours, minutes, seconds } = parseTime(time, translateCommon);

    if (hours < 5 && hours >= 0 && withDp1) {
        return moment.duration({ hours, minutes, seconds }).add("24", "h");
    }
    return moment.duration({ hours, minutes, seconds });
}

/**
 * Format a LT time to a duration
 * @param time Time to format
 * @returns Duration
 */
export function formatLtTimeToDuration(
    time: string,
    translateCommon: TFunction
): moment.Duration | null | undefined {
    if (time === null || time === undefined) {
        return time as null | undefined;
    }

    const { hours, minutes, seconds } = parseLtTime(time, translateCommon);

    if (hours < 5 && hours >= 0) {
        return moment.duration({ hours, minutes, seconds }).add("24", "h");
    }

    return moment.duration({ hours, minutes, seconds });
}

/**
 * Convert a duration to date
 * @param input Duration input
 * @param refDate Referential date
 * @param unit Unit for duration
 * @returns Moment date
 */
export function formatDurationToDateTime(
    input: moment.DurationInputArg1,
    refDate?: moment.MomentInput,
    unit?: moment.DurationInputArg2
): moment.Moment {
    const duration = moment.duration(input, unit);
    const dateTime = moment(refDate).startOf("day").add(duration);

    return dateTime;
}

/**
 * Format two durations into a slot
 * @param startDuration Start duration for slot
 * @param endDuration End duration for slot
 * @param opts Options
 * @returns Slot formatted string
 */
export function formatDurationToSlot(
    startDuration: moment.DurationInputArg1,
    endDuration: moment.DurationInputArg1,
    translateCommon: TFunction
): string {
    if (
        (startDuration === null || startDuration === undefined) &&
        (endDuration === null || endDuration === undefined)
    ) {
        return undefined;
    }

    const startMomentDuration = moment.duration(startDuration);
    const startDays = startMomentDuration.days();
    const startHours = startMomentDuration.hours();
    const startMinutes = startMomentDuration.minutes();
    const dp1Start =
        startHours + startDays * 24 >= 24 ? ` (${translateCommon("dates.dp1-suffix")})` : "";
    const formattedDurationStart = moment(`${startHours}:${startMinutes}`, FORMAT_TIME)
        .format(FORMAT_TIME)
        .concat(dp1Start);

    const endMomentDuration = moment.duration(endDuration);
    const endDays = endMomentDuration.days();
    const endHours = endMomentDuration.hours();
    const endMinutes = endMomentDuration.minutes();
    const dp1End = endHours + endDays * 24 >= 24 ? ` (${translateCommon("dates.dp1-suffix")})` : "";
    const formattedDurationEnd = moment(`${endHours}:${endMinutes}`, FORMAT_TIME)
        .format(FORMAT_TIME)
        .concat(dp1End);

    return [formattedDurationStart, formattedDurationEnd].filter(Boolean).join(" - ");
}

/**
 * Return dp1-suffix if needed
 * @param slotDateUTC date of slot
 * @param shiftedDate Shifted date
 * @returns dp1-suffix or empty string
 */
export function getDPSuffixForSlotsFromString(
    slotDateUTC: string,
    shiftedDate: string,
    timezone: string,
    translateCommon: TFunction
): string {
    if (!shiftedDate) {
        return "";
    }

    const slotMoment = moment(slotDateUTC).tz(timezone);
    const shiftedMoment = moment.tz(shiftedDate, timezone);

    return slotMoment.isSame(shiftedMoment, "day")
        ? ""
        : ` (${translateCommon("dates.dp1-suffix")})`;
}

export function formatOldTimeToNewTime(time: number, roundToHalfHour = false): number {
    if (Number.isInteger(time)) {
        return time;
    }
    const decimalPart = Number(time?.toString().split(".")[1]);
    if (decimalPart === 3) {
        return Math.floor(time) + 0.5;
    }

    // Cas où les minutes sont des dizaines. Ex : 2:10 / 12:40
    if (decimalPart.toString().length === 1) {
        const newDecimalPart = (0.5 * decimalPart) / 3;
        if (roundToHalfHour) {
            return Math.floor(time) + Math.round(newDecimalPart * 2) / 2;
        }
        return Math.floor(time) + newDecimalPart;
    }

    // Du coup dans ce cas les minutes ne le sont pas
    const newDecimalPart = (0.5 * decimalPart) / 30;
    if (roundToHalfHour) {
        return Math.floor(time) + Math.round(newDecimalPart * 2) / 2;
    }
    return Math.floor(time) + (0.5 * decimalPart) / 30;
}

export function convertTimeStringToDuration(
    time: string | null | undefined
): moment.Duration | null | undefined {
    if (typeof time !== "string") {
        return time;
    }

    const [hours, minutes, seconds = 0] = time.split(":").map((v) => Number.parseInt(v, 10));

    return moment.duration({ hours, minutes, seconds });
}

/**
 * Return formatted hour with +24 hours if needed
 * @param hours Number of hours to format
 * @returns formattedHours number
 */
export function formatHoursPlus24(hours: number) {
    if (hours >= 0 && hours < 5) {
        return hours + 24;
    }

    return hours;
}

export function formatDurationToTimeNumber(duration: moment.Duration | string): number {
    if (isNil(duration)) {
        return 0;
    }

    const durationMoment = typeof duration === "string" ? moment.duration(duration) : duration;
    const hours = durationMoment?.hours();
    const minutes = durationMoment?.minutes();

    const number = Number.parseFloat(`${hours}.${minutes}`);
    return number;
}
